import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Breadcrumb from "../../components/Breadcrumb";

// ── API Endpoints ─────────────────────────────────────────────────────────────
const GET_DOMAINS_API =
    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/get_user_domain.php";
const STATUS_CHANGE_API =
    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/user_domain_status_change.php";
const DELETE_DOMAIN_API =
    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/user_domain_delete.php";

// ── Constants ─────────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20];

// ── Helpers ───────────────────────────────────────────────────────────────────
const truncateUrl = (url = "", maxLen = 45) =>
    url.length > maxLen ? url.slice(0, maxLen) + "…" : url;

const mapApiItem = (item) => ({
    id: String(item.domain_id ?? ""),
    userId: String(item.user_id ?? ""),
    appName: item.app_name ?? "—",
    appUrl: item.domain_name ?? "",
    userName: item.full_name ?? "—",
    userEmail: item.email ?? "—",
    status:
        String(item.domain_status) === "1"
            ? "accepted"
            : String(item.domain_status) === "2"
                ? "rejected"
                : "pending",
});

const isApiSuccess = (json) => {
    if (!json || typeof json !== "object") return false;
    const { status, success, error } = json;
    const code = json.code ?? json.status_code ?? json.statusCode;
    const message = String(json.message ?? json.msg ?? "").toLowerCase();

    if (error === true || error === 1 || error === "1") return false;
    if (status === false || status === 0 || status === "0" || status === "false") return false;
    if (success === false || success === 0 || success === "0" || success === "false") return false;

    if (status === true || status === 1 || status === "1" || status === "true") return true;
    if (success === true || success === 1 || success === "1" || success === "true") return true;
    if (code === 200 || code === "200") return true;

    if (["success", "updated", "changed", "accepted", "rejected", "deleted", "done", "ok"]
        .some((k) => message.includes(k))) return true;

    return true;
};

const getApiMessage = (json) =>
    json?.message ?? json?.msg ?? json?.error_message ?? null;

// ── Tiny reusable UI pieces ───────────────────────────────────────────────────
const Spinner = ({ className = "h-3 w-3" }) => (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10"
            stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
);

const StatusBadge = ({ status }) => {
    if (status === "accepted")
        return (
            <span className="inline-flex items-center gap-1 text-orange-700 font-semibold text-xs bg-orange-100 px-3 py-1 rounded-full">
                ✓ Accepted
            </span>
        );
    if (status === "rejected")
        return (
            <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-xs bg-red-100 px-3 py-1 rounded-full">
                ✕ Rejected
            </span>
        );
    return (
        <span className="inline-flex items-center gap-1 text-yellow-700 font-semibold text-xs bg-yellow-100 px-3 py-1 rounded-full">
            ⏳ Pending
        </span>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function DomainRequests() {

    const token = useSelector((state) => state.user.userToken);
    const loggedInUser = useSelector((state) => state.user);

    // ── Table data state ──────────────────────────────────────────────────────
    const [requests, setRequests] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    // ── Per-row action loading state ──────────────────────────────────────────
    const [loadingId, setLoadingId] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // ── Toast notification ────────────────────────────────────────────────────
    const [toast, setToast] = useState(null);

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // ── Confirm modals ────────────────────────────────────────────────────────
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmReject, setConfirmReject] = useState(null);

    // ── Derived pagination values ─────────────────────────────────────────────
    const isBusy = (id) => loadingId === id || rejectingId === id || deletingId === id;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    // ── Toast helper ──────────────────────────────────────────────────────────
    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    // ── Fetch domain list ─────────────────────────────────────────────────────
    const fetchDomains = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const res = await fetch(GET_DOMAINS_API, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": token,
                },
                body: JSON.stringify({
                    perpage: itemsPerPage,
                    page: currentPage - 1,
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const json = await res.json();
            const rows = json.data ?? [];
            const total = json.total_record ?? json.total ?? json.total_count ?? rows.length;

            setRequests(rows.map(mapApiItem));
            setTotalItems(Number(total));
        } catch (err) {
            setFetchError(err.message || "Failed to load data.");
            setRequests([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, token]);

    useEffect(() => { fetchDomains(); }, [fetchDomains]);

    // ── Shared status-change API call ─────────────────────────────────────────
    const changeStatus = async (req, domainStatus) => {
        const resolvedUserId = String(
            req.userId ||
            loggedInUser?.userId ||
            loggedInUser?.id ||
            loggedInUser?.user_id ||
            ""
        );

        const payload = {
            domain_id: String(req.id),
            user_id: resolvedUserId,
            domain_status: String(domainStatus),
            domain_name: req.appUrl,
        };

        console.log("✅ [changeStatus] PAYLOAD →", JSON.stringify(payload, null, 2));
        console.log("✅ [changeStatus] TOKEN   →", token);

        const res = await fetch(STATUS_CHANGE_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token,
            },
            body: JSON.stringify(payload),
        });

        let json = null;
        try { json = await res.json(); } catch { /* non-JSON body */ }

        console.log("✅ [changeStatus] RESPONSE →", json);

        if (!res.ok) {
            throw new Error(getApiMessage(json) || `Server error: HTTP ${res.status}`);
        }
        if (json !== null && !isApiSuccess(json)) {
            throw new Error(getApiMessage(json) || "Failed to process. Please try again.");
        }
    };

    // ── Accept ────────────────────────────────────────────────────────────────
    const handleAccept = async (req) => {
        setLoadingId(req.id);
        try {
            await changeStatus(req, "1");
            setRequests((prev) =>
                prev.map((r) => (r.id === req.id ? { ...r, status: "accepted" } : r))
            );
            showToast(`Domain "${req.appName}" accepted successfully!`);
        } catch (err) {
            showToast(err.message || "Failed to accept domain. Try again.", "error");
        } finally {
            setLoadingId(null);
        }
    };

    // ── Reject (after modal confirm) ──────────────────────────────────────────
    const handleRejectConfirm = async () => {
        const req = confirmReject;
        setConfirmReject(null);
        setRejectingId(req.id);
        try {
            await changeStatus(req, "2");
            setRequests((prev) =>
                prev.map((r) => (r.id === req.id ? { ...r, status: "rejected" } : r))
            );
            showToast(`"${req.appName}" rejected.`, "error");
        } catch (err) {
            showToast(err.message || "Failed to reject. Try again.", "error");
        } finally {
            setRejectingId(null);
        }
    };

    // ── Delete (after modal confirm) ──────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        const req = confirmDelete;
        setConfirmDelete(null);        // close modal immediately
        setDeletingId(req.id);

        // ✅ Resolve user_id — same fallback chain used in changeStatus
        const resolvedUserId = String(
            req.userId ||
            loggedInUser?.userId ||
            loggedInUser?.id ||
            loggedInUser?.user_id ||
            ""
        );

        // ✅ Payload: exactly domain_id + user_id
        const payload = {
            domain_id: String(req.id),
            user_id: resolvedUserId,
        };

        console.log("✅ [handleDeleteConfirm] PAYLOAD →", JSON.stringify(payload, null, 2));
        console.log("✅ [handleDeleteConfirm] TOKEN   →", token);

        try {
            const res = await fetch(DELETE_DOMAIN_API, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": token,          // ✅ token in header, NOT body
                },
                body: JSON.stringify(payload),
            });

            let json = null;
            try { json = await res.json(); } catch { /* non-JSON body */ }

            console.log("✅ [handleDeleteConfirm] RESPONSE →", json);

            if (!res.ok) {
                throw new Error(getApiMessage(json) || `Server error: HTTP ${res.status}`);
            }
            if (json !== null && !isApiSuccess(json)) {
                throw new Error(getApiMessage(json) || "Delete failed. Please try again.");
            }

            // ✅ Optimistically remove row from UI, then re-fetch to sync pagination
            setRequests((prev) => prev.filter((r) => r.id !== req.id));
            setTotalItems((prev) => Math.max(0, prev - 1));

            showToast(`"${req.appName}" deleted successfully.`);

            // Re-fetch to keep page totals/pagination in sync with server
            await fetchDomains();

        } catch (err) {
            console.error("Delete error:", err);
            showToast(err.message || "Failed to delete. Try again.", "error");
        } finally {
            setDeletingId(null);
        }
    };

    // ── Pagination controls ───────────────────────────────────────────────────
    const goFirst = () => setCurrentPage(1);
    const goPrev = () => setCurrentPage((p) => Math.max(1, p - 1));
    const goNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
    const goLast = () => setCurrentPage(totalPages);
    const handleRowsChange = (e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); };

    const NavBtn = ({ onClick, disabled, children, title }) => (
        <button onClick={onClick} disabled={disabled} title={title}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-500
        hover:text-gray-800 hover:bg-gray-100 disabled:opacity-30
        disabled:cursor-not-allowed transition-colors">
            {children}
        </button>
    );

    // ── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-100 font-sans">

            <Breadcrumb pageName="Domain Requests" />

            <div className="py-6 mx-auto">

                {/* ── Table Card ─────────────────────────────────────────────────────── */}
                <div className="bg-white rounded-lg shadow overflow-hidden">

                    {/* Loading State */}
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
                            <Spinner className="h-5 w-5" />
                            <span className="text-sm">Loading…</span>
                        </div>
                    )}

                    {/* Error State */}
                    {!loading && fetchError && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <p className="text-sm text-red-500">{fetchError}</p>
                            <button onClick={fetchDomains}
                                className="text-xs px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors">
                                Retry
                            </button>
                        </div>
                    )}

                    {/* ── Data Table ──────────────────────────────────────────────────── */}
                    {!loading && !fetchError && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead>
                                    <tr className="bg-[#f97316] text-white uppercase text-xs font-semibold tracking-wide">
                                        <th className="px-4 py-3 text-white">#</th>
                                        <th className="px-4 py-3 text-white">App Name</th>
                                        <th className="px-4 py-3 text-white">App URL</th>
                                        <th className="px-4 py-3 text-white">User Name</th>
                                        <th className="px-4 py-3 text-white">User Email</th>
                                        <th className="px-4 py-3 text-center text-white">Status</th>
                                        <th className="px-4 py-3 text-center text-white">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {requests.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-10 text-gray-400">
                                                No domain requests found.
                                            </td>
                                        </tr>
                                    ) : (
                                        requests.map((req, idx) => (
                                            <tr key={req.id}
                                                className={`transition-colors ${deletingId === req.id
                                                    ? "opacity-40 pointer-events-none bg-red-50"
                                                    : req.status === "accepted"
                                                        ? "hover:bg-orange-50 bg-orange-50"
                                                        : req.status === "rejected"
                                                            ? "hover:bg-red-50 bg-red-50"
                                                            : "hover:bg-gray-50"
                                                    }`}>

                                                <td className="px-4 py-3 text-gray-400 text-xs">{startIndex + idx + 1}</td>
                                                <td className="px-4 py-3 font-medium text-gray-800">{req.appName}</td>
                                                <td className="px-4 py-3">
                                                    {req.appUrl ? (
                                                        <a href={req.appUrl} target="_blank" rel="noopener noreferrer"
                                                            className="text-orange-600 hover:text-orange-800 hover:underline"
                                                            title={req.appUrl}>
                                                            {truncateUrl(req.appUrl)}
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-300 text-xs italic">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-700">{req.userName}</td>
                                                <td className="px-4 py-3 text-gray-600">{req.userEmail}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <StatusBadge status={req.status} />
                                                </td>

                                                {/* ── Actions cell ── */}
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-2">

                                                        {/* Accept / Reject — only for pending */}
                                                        {req.status === "pending" && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleAccept(req)}
                                                                    disabled={isBusy(req.id)}
                                                                    className="inline-flex items-center gap-1 bg-orange-600
                                                                        hover:bg-orange-700 disabled:bg-orange-400 text-white
                                                                        text-xs font-semibold px-3 py-1.5 rounded transition-colors">
                                                                    {loadingId === req.id
                                                                        ? <><Spinner /> Accepting…</>
                                                                        : "Accept"}
                                                                </button>
                                                                <button
                                                                    onClick={() => setConfirmReject(req)}
                                                                    disabled={isBusy(req.id)}
                                                                    className="inline-flex items-center gap-1 bg-orange-500
                                                                        hover:bg-orange-600 disabled:bg-orange-300 text-white
                                                                        text-xs font-semibold px-3 py-1.5 rounded transition-colors">
                                                                    {rejectingId === req.id
                                                                        ? <><Spinner /> Rejecting…</>
                                                                        : "Reject"}
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* Delete button — always visible */}
                                                        {deletingId === req.id ? (
                                                            <Spinner className="h-4 w-4 text-red-400" />
                                                        ) : (
                                                            <button
                                                                onClick={() => setConfirmDelete(req)}
                                                                disabled={isBusy(req.id)}
                                                                title="Delete"
                                                                className="p-1.5 rounded text-red-400 hover:text-red-600
                                                                    hover:bg-red-50 disabled:opacity-40
                                                                    disabled:cursor-not-allowed transition-colors">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                                    viewBox="0 0 24 24" strokeWidth="2">
                                                                    <path strokeLinecap="round" strokeLinejoin="round"
                                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0
                                                                        01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0
                                                                        00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Pagination Bar ───────────────────────────────────────────────── */}
                    {!loading && !fetchError && (
                        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-end gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-2">
                                <span className="whitespace-nowrap">Rows per page:</span>
                                <div className="relative">
                                    <select value={itemsPerPage} onChange={handleRowsChange}
                                        className="appearance-none border-0 bg-transparent pr-4 pl-1 py-1
                                        text-xs text-gray-700 font-medium cursor-pointer focus:outline-none">
                                        {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                    <svg className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            <span className="whitespace-nowrap text-gray-600">
                                {totalItems === 0 ? "0" : startIndex + 1}–{endIndex} of {totalItems}
                            </span>
                            <div className="flex items-center gap-0.5">
                                <NavBtn onClick={goFirst} disabled={currentPage === 1} title="First page">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
                                    </svg>
                                </NavBtn>
                                <NavBtn onClick={goPrev} disabled={currentPage === 1} title="Previous page">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </NavBtn>
                                <NavBtn onClick={goNext} disabled={currentPage === totalPages || totalPages === 0} title="Next page">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </NavBtn>
                                <NavBtn onClick={goLast} disabled={currentPage === totalPages || totalPages === 0} title="Last page">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M6 5l7 7-7 7" />
                                    </svg>
                                </NavBtn>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Reject Confirm Modal ──────────────────────────────────────────────── */}
            {confirmReject && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-800">Reject Domain Request</h3>
                                <p className="text-xs text-gray-500 mt-0.5">The user will be notified.</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-5">
                            Are you sure you want to reject{" "}
                            <span className="font-semibold text-gray-800">"{confirmReject.appName}"</span>?
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setConfirmReject(null)}
                                className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleRejectConfirm}
                                className="px-4 py-2 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm Modal ──────────────────────────────────────────────── */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0
                                        01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0
                                        00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-800">Delete Domain Request</h3>
                                <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-5">
                            Are you sure you want to delete{" "}
                            <span className="font-semibold text-gray-800">"{confirmDelete.appName}"</span>?
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleDeleteConfirm}
                                className="px-4 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Toast Notification ────────────────────────────────────────────────── */}
            {toast && (
                <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-lg shadow-lg
                    text-sm font-medium text-white transition-all
                    ${toast.type === "error" ? "bg-red-500" : "bg-orange-500"}`}>
                    {toast.msg}
                </div>
            )}

        </div>
    );
}